import { useEffect, useState } from "react";
import { HealthResponseSchema, type HealthResponse } from "@hyphesync/shared";

type Status =
  | { kind: "loading" }
  | { kind: "ok"; health: HealthResponse }
  | { kind: "error"; message: string };

const API = import.meta.env.VITE_API_BASE_URL ?? "";

/** M1.0 placeholder: proves the SPA → API path end to end. Sign-in arrives in M1.1. */
export function App() {
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  useEffect(() => {
    let alive = true;
    // A mount load MUST handle rejection: a transport failure rejects with no status, and an
    // unhandled one wedges the page (SunshineBids §4).
    (async () => {
      try {
        const res = await fetch(`${API}/api/health`);
        if (!res.ok) throw new Error(`API answered ${res.status}`);
        const health = HealthResponseSchema.parse(await res.json());
        if (alive) setStatus({ kind: "ok", health });
      } catch (e) {
        if (alive) setStatus({ kind: "error", message: e instanceof Error ? e.message : "Unknown error" });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <main className="mx-auto flex min-h-screen max-w-xl flex-col justify-center gap-6 px-4">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">HypheSync</h1>
        <p className="mt-1 text-muted">Fleet hub — telematics and maintenance in one place.</p>
      </div>
      <section aria-labelledby="api-status" className="rounded-xl border border-line bg-card p-5">
        <h2 id="api-status" className="text-sm font-medium text-muted">API status</h2>
        {status.kind === "loading" && <p className="mt-2">Checking…</p>}
        {status.kind === "ok" && (
          <p className="mt-2">
            <span className="font-medium text-ok">Online</span> · {status.health.environment} · v
            {status.health.version}
          </p>
        )}
        {status.kind === "error" && (
          <p className="mt-2">
            <span className="font-medium text-bad">Unreachable</span> · {status.message}
          </p>
        )}
      </section>
      <p className="text-sm text-muted">Sign-in arrives with the next release.</p>
    </main>
  );
}
